import React, { useState, useEffect } from 'react';
import styles from './EditEmployeeModal.module.css';
import { doc, updateDoc, collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { FaUserEdit, FaTimes, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { EMPLOYEE_ROLE_OPTIONS } from '../../constants/employeeGroups';
import {
    PROFESSION_OPTIONS,
    parseLanguageInfo,
    formatLanguageInfo
} from '../../constants/employeeOptions';
import LanguageInfoField from './LanguageInfoField';

// Yetkilendirme alanını yalnızca bu kullanıcı düzenleyebilir
const AUTHORIZATION_EDITOR_EMAIL = 'turanemre1999@gmail.com';

const workTypeOptions = [
  'Full Time',
  'Part Time'
];

const educationLevelOptions = [
  'Ortaokul / Ortaöğretim',
  'Lise',
  'Ön Lisans (2 Yıllık Üniversite)',
  'Lisans (4 Yıllık Üniversite)',
  'Yüksek Lisans (Master)',
  'Doktora (PhD)'
];

const statusOptions = [
  { value: 1, label: 'Aktif' },
  { value: 0, label: 'İnaktif' }
];

const yesNoOptions = [
  { value: 'Yes', label: 'Evet' },
  { value: 'No', label: 'Hayır' }
];

const nationalityOptions = [
  'Türk',
  'Afganlı',
  'Alman',
  'Amerikalı',
  'Arap',
  'Arnavut',
  'Azeri',
  'Belçikalı',
  'Boşnak',
  'Bulgar',
  'Çinli',
  'Fransız',
  'Gürcü',
  'Hintli',
  'Hollandalı',
  'İngiliz',
  'İranlı',
  'İspanyol',
  'İsveçli',
  'İtalyan',
  'Japon',
  'Kazak',
  'Kırgız',
  'Koreli',
  'Lübnanlı',
  'Makedon',
  'Mısırlı',
  'Moldovalı',
  'Özbek',
  'Pakistanlı',
  'Polonyalı',
  'Portekizli',
  'Romanyalı',
  'Rus',
  'Sırp',
  'Suriyeli',
  'Tacik',
  'Tunuslu',
  'Türkmen',
  'Ukraynalı',
  'Ürdünlü',
  'Yunan',
  'Diğer'
];

const EditEmployeeModal = ({ selectedEmployee, onClose, onUpdate }) => {
    const { isDarkMode } = useTheme();
    const { userEmail, userAuthorization } = useUserProfile();
    
    // Yetkilendirme alanını düzenleyebilecek kullanıcılar:
    // 1. Super admin (turanemre1999@gmail.com)
    // 2. admin rolüne sahip olanlar
    // 3. Kısıtlı Admin rolüne sahip olanlar
    const canEditAuthorization =
        String(userEmail || '').trim().toLowerCase() === AUTHORIZATION_EDITOR_EMAIL ||
        (userAuthorization && (
            userAuthorization.includes('admin') || 
            userAuthorization.includes('Kısıtlı Admin')
        ));
    
    const [departments, setDepartments] = useState([]);
    const [authorizationRoles, setAuthorizationRoles] = useState([]);
    const [isLoadingDepartments, setIsLoadingDepartments] = useState(true);
    const [isLoadingAuthorizationRoles, setIsLoadingAuthorizationRoles] = useState(true);
    const [formData, setFormData] = useState({
        documentId: selectedEmployee.id,
        eName: selectedEmployee.eName || '',
        eSurname: selectedEmployee.eSurname || '',
        eMail: selectedEmployee.eMail || '',
        phone: selectedEmployee.phone || '',
        emergencyPhone: selectedEmployee.emergencyPhone || '', // Acil durum telefonu
        address: selectedEmployee.address || '',
        departman: selectedEmployee.departman || '',
        authorization: selectedEmployee.authorization || '',
        workType: selectedEmployee.workType || '',
        Active: selectedEmployee.Active !== undefined ? selectedEmployee.Active : 1,
        ID: selectedEmployee.ID || '',
        annualPermit: selectedEmployee.annualPermit || '',
        birthday: selectedEmployee.birthday || '',
        isHandicappedPerson: selectedEmployee.isHandicappedPerson || 'No',
        isIntern: selectedEmployee.isIntern || 'No',
        isRetired: selectedEmployee.isRetired || 'No',
        languageInfo: selectedEmployee.languageInfo || '',
        nationality: selectedEmployee.nationality || '',
        profession: selectedEmployee.profession || '',
        educationInfo: selectedEmployee.educationInfo || '',
        startDate: selectedEmployee.startDate || '',
        tcNumber: selectedEmployee.tcNumber || '',
        role: selectedEmployee.role || '',
    });

    // Dil bilgisi editörünün çalışma listesi; kayıt formatına (string) çevrimi
    // aşağıdaki effect formData.languageInfo üzerinden yapar
    const [languageEntries, setLanguageEntries] = useState(() =>
        parseLanguageInfo(selectedEmployee.languageInfo)
    );

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            languageInfo: formatLanguageInfo(languageEntries)
        }));
    }, [languageEntries]);

    useEffect(() => {
        fetchDepartments();
        fetchAuthorizationRoles();
    }, []);

    const fetchDepartments = async () => {
        try {
            setIsLoadingDepartments(true);
            const employeeDepartmentsRef = collection(db, 'DataEntryValues', 'departments', 'EmployeeDepartments');
            const q = query(employeeDepartmentsRef, orderBy('id', 'asc'));
            const querySnapshot = await getDocs(q);
            
            const fetchedDepartments = querySnapshot.docs.map(doc => doc.data().value);
            setDepartments(fetchedDepartments);
        } catch (error) {
            console.error('Error fetching departments:', error);
            toast.error('Departmanlar yüklenirken bir hata oluştu.');
        } finally {
            setIsLoadingDepartments(false);
        }
    };

    const fetchAuthorizationRoles = async () => {
        try {
            setIsLoadingAuthorizationRoles(true);
            const authorizationRolesRef = collection(db, 'DataEntryValues', 'roles', 'AuthorizationRoles');
            const q = query(authorizationRolesRef, orderBy('id', 'asc'));
            const querySnapshot = await getDocs(q);
            
            const fetchedRoles = querySnapshot.docs.map(doc => doc.data().value);
            setAuthorizationRoles(fetchedRoles);
        } catch (error) {
            console.error('Error fetching authorization roles:', error);
            toast.error('Yetkilendirme rolleri yüklenirken bir hata oluştu.');
        } finally {
            setIsLoadingAuthorizationRoles(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Check if email already exists (only if email is provided and changed)
            if (formData.eMail && formData.eMail.trim() !== '') {
                const trimmedEmail = formData.eMail.trim();
                
                // Only check if email has changed from the original
                if (trimmedEmail !== selectedEmployee.eMail) {
                    const emailQuery = query(
                        collection(db, 'Employees'),
                        where('eMail', '==', trimmedEmail)
                    );
                    const emailSnapshot = await getDocs(emailQuery);
                    
                    if (!emailSnapshot.empty) {
                        toast.error('Bu e-posta adresi ile kayıtlı bir çalışan zaten mevcut!');
                        return;
                    }
                }
            }

            const employeeRef = doc(db, 'Employees', selectedEmployee.id);
            
            const { documentId, ...updateData } = formData;

            // Yetkilendirme alanını yalnızca yetkili kullanıcı değiştirebilir
            if (!canEditAuthorization) {
                delete updateData.authorization;
            }

            await updateDoc(employeeRef, updateData);
            toast.success('Çalışan bilgileri başarıyla güncellendi');
            await onUpdate();
            onClose();
        } catch (error) {
            console.error('Error updating employee:', error);
            toast.error('Çalışan güncellenirken hata oluştu: ' + error.message);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'Active') {
            setFormData((prevState) => ({
                ...prevState,
                [name]: parseInt(value),
            }));
        } else {
            setFormData((prevState) => ({
                ...prevState,
                [name]: value,
            }));
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div 
            className={styles.modalOverlay} 
            onClick={handleOverlayClick}
        >
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        <FaUserEdit className={styles.modalIcon} />
                        Çalışanı Düzenle
                    </h2>
                    <button 
                        className={styles.closeButton} 
                        onClick={onClose}
                        aria-label="Kapat"
                    >
                        <FaTimes />
                    </button>
                </div>
                
                <div className={styles.modalBody}>
                    <form onSubmit={handleSubmit} id="edit-employee-form">
                        <div className={styles.formGrid}>
                            {/* Personal Information Section */}
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Sicil Numarası:</label>
                                <input
                                    type="text"
                                    name="ID"
                                    value={formData.ID}
                                    onChange={handleInputChange}
                                    className={styles.formInput}
                                />
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>İsim:</label>
                                <input
                                    type="text"
                                    name="eName"
                                    value={formData.eName}
                                    onChange={handleInputChange}
                                    className={styles.formInput}
                                />
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Soyisim:</label>
                                <input
                                    type="text"
                                    name="eSurname"
                                    value={formData.eSurname}
                                    onChange={handleInputChange}
                                    className={styles.formInput}
                                />
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>TC Kimlik No:</label>
                                <input
                                    type="text"
                                    name="tcNumber"
                                    value={formData.tcNumber}
                                    onChange={handleInputChange}
                                    className={styles.formInput}
                                />
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Uyruk:</label>
                                <select
                                    name="nationality"
                                    value={formData.nationality}
                                    onChange={handleInputChange}
                                    className={styles.formSelect}
                                >
                                    <option value="">Seçiniz</option>
                                    {nationalityOptions.map((option, index) => (
                                        <option key={index} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Doğum Tarihi:</label>
                                <input
                                    type="date"
                                    name="birthday"
                                    value={formData.birthday}
                                    onChange={handleInputChange}
                                    className={styles.formInput}
                                />
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>İşe Başlama Tarihi:</label>
                                <input
                                    type="date"
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleInputChange}
                                    className={styles.formInput}
                                />
                            </div>

                            {/* Contact Information */}
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Email:</label>
                                <input
                                    type="email"
                                    name="eMail"
                                    value={formData.eMail}
                                    onChange={handleInputChange}
                                    className={styles.formInput}
                                />
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Telefon:</label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    className={styles.formInput}
                                    placeholder="Örn: 532 123 45 67"
                                />
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Acil Durum Telefonu:</label>
                                <input
                                    type="text"
                                    name="emergencyPhone"
                                    value={formData.emergencyPhone}
                                    onChange={handleInputChange}
                                    className={styles.formInput}
                                    placeholder="Acil durum için telefon (opsiyonel)"
                                />
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Adres:</label>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    className={styles.formInput}
                                />
                            </div>

                            {/* Work Information */}
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Departman:</label>
                                <select
                                    name="departman"
                                    value={formData.departman}
                                    onChange={handleInputChange}
                                    className={styles.formSelect}
                                    disabled={isLoadingDepartments}
                                >
                                    <option value="">Seçiniz</option>
                                    {departments.map((department) => (
                                        <option key={department} value={department}>
                                            {department}
                                        </option>
                                    ))}
                                </select>
                                {isLoadingDepartments && (
                                    <div className={styles.loadingIndicator}>Departmanlar yükleniyor...</div>
                                )}
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Yetkilendirme:</label>
                                <select
                                    name="authorization"
                                    value={formData.authorization}
                                    onChange={handleInputChange}
                                    className={styles.formSelect}
                                    disabled={isLoadingAuthorizationRoles || !canEditAuthorization}
                                    title={canEditAuthorization ? undefined : 'Bu alanı yalnızca süper admin düzenleyebilir'}
                                >
                                    <option value="">Seçiniz</option>
                                    {authorizationRoles.map((role) => (
                                        <option key={role} value={role}>
                                            {role}
                                        </option>
                                    ))}
                                </select>
                                {isLoadingAuthorizationRoles && (
                                    <div className={styles.loadingIndicator}>Roller yükleniyor...</div>
                                )}
                                {!canEditAuthorization && (
                                    <div className={styles.loadingIndicator}>
                                        🔒 Bu alanı yalnızca süper admin düzenleyebilir
                                    </div>
                                )}
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Meslek:</label>
                                <select
                                    name="profession"
                                    value={formData.profession}
                                    onChange={handleInputChange}
                                    className={styles.formSelect}
                                >
                                    <option value="">Seçiniz</option>
                                    {formData.profession &&
                                        !PROFESSION_OPTIONS.includes(formData.profession) && (
                                        <option value={formData.profession}>
                                            {formData.profession} (liste dışı)
                                        </option>
                                    )}
                                    {PROFESSION_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Eğitim Bilgisi:</label>
                                <select
                                    name="educationInfo"
                                    value={formData.educationInfo}
                                    onChange={handleInputChange}
                                    className={styles.formSelect}
                                >
                                    <option value="">Seçiniz</option>
                                    {educationLevelOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Dil Bilgisi:</label>
                                <LanguageInfoField
                                    entries={languageEntries}
                                    onChange={setLanguageEntries}
                                    styles={styles}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Çalışan Rolü:</label>
                                {/* Rol, eğitim/SOP/form atamalarındaki grup adlarıyla birebir
                                    eşleşmek zorunda olduğundan serbest metin yerine listeden seçilir */}
                                <select
                                    name="role"
                                    value={formData.role}
                                    onChange={handleInputChange}
                                    className={styles.formSelect}
                                >
                                    <option value="">Seçiniz</option>
                                    {formData.role &&
                                        !EMPLOYEE_ROLE_OPTIONS.includes(formData.role) && (
                                        <option value={formData.role}>
                                            {formData.role} (liste dışı)
                                        </option>
                                    )}
                                    {EMPLOYEE_ROLE_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                                {formData.role && !EMPLOYEE_ROLE_OPTIONS.includes(formData.role) && (
                                    <div className={styles.loadingIndicator}>
                                        ⚠️ Mevcut rol listede yok; eğitim ve SOP atamaları bu rolle eşleşmez.
                                    </div>
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Çalışma Şekli:</label>
                                <select
                                    name="workType"
                                    value={formData.workType}
                                    onChange={handleInputChange}
                                    className={styles.formSelect}
                                >
                                    <option value="">Seçiniz</option>
                                    {workTypeOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Yıllık İzin:</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    name="annualPermit"
                                    value={formData.annualPermit}
                                    onChange={handleInputChange}
                                    className={styles.formInput}
                                />
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Durum:</label>
                                <select
                                    name="Active"
                                    value={formData.Active}
                                    onChange={handleInputChange}
                                    className={styles.formSelect}
                                >
                                    {statusOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Engellilik Durumu:</label>
                                <select
                                    name="isHandicappedPerson"
                                    value={formData.isHandicappedPerson}
                                    onChange={handleInputChange}
                                    className={styles.formSelect}
                                >
                                    {yesNoOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Stajyer Mi?</label>
                                <select
                                    name="isIntern"
                                    value={formData.isIntern}
                                    onChange={handleInputChange}
                                    className={styles.formSelect}
                                >
                                    {yesNoOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Emekli Mi?</label>
                                <select
                                    name="isRetired"
                                    value={formData.isRetired}
                                    onChange={handleInputChange}
                                    className={styles.formSelect}
                                >
                                    {yesNoOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </form>
                </div>
                
                <div className={styles.formActions}>
                    <button 
                        type="button" 
                        className={styles.buttonCancel} 
                        onClick={onClose}
                    >
                        İptal
                    </button>
                    <button 
                        type="submit" 
                        form="edit-employee-form"
                        className={styles.buttonSave}
                    >
                        <FaSave className={styles.buttonIcon} />
                        Kaydet
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditEmployeeModal;